import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Box, Flex, QuoteBox, Text } from '@/components/common';
import { formatRelativeTimestamp } from '@/utils/date';
import { MessageLinkify } from './MessageLinkify';

interface ChatBubbleProps {
  variant: 'mine' | 'theirs';
  createdAt?: string;
  quote?: { text: string };
  children: ReactNode;
}

export function ChatBubble({ variant, createdAt, quote, children }: ChatBubbleProps) {
  const mine = variant === 'mine';
  const { t } = useTranslation('common');
  const timestamp = formatRelativeTimestamp(createdAt, {
    includeTime: true,
    yesterdayLabel: t('time.yesterday'),
  });

  return (
    <Flex direction="column" gap="1" align={mine ? 'end' : 'start'} maxWidth="80%" minWidth="0">
      {quote && (
        <QuoteBox strong={mine}>
          <Text size="0.5" color={mine ? undefined : 'gray'} style={{ overflowWrap: 'anywhere' }}>
            {quote.text}
          </Text>
        </QuoteBox>
      )}
      <Box
        px="2"
        py="1"
        maxWidth="100%"
        minWidth="0"
        data-accent-color={mine ? 'red' : 'gray'}
        style={{
          borderRadius: 'var(--radius-btn)',
          ...(mine
            ? {
                backgroundColor: 'var(--accent-9)',
                borderBottomRightRadius: 'var(--radius-xs)',
              }
            : {
                backgroundColor: 'var(--color-panel)',
                border: '1px solid var(--accent-a6)',
                borderTopLeftRadius: 'var(--radius-xs)',
              }),
        }}
      >
        <Text size="2" highContrast={!mine} style={{ overflowWrap: 'anywhere' }}>
          <MessageLinkify>{children}</MessageLinkify>
        </Text>
      </Box>
      {timestamp && (
        <Text as="span" size="0.5" color="gray" highContrast={mine}>
          {timestamp}
        </Text>
      )}
    </Flex>
  );
}
